import { useRef, useEffect, useCallback } from 'react';
import {
  StripeTerminal,
  ReaderInterface,
  TerminalEventsEnum,
  TerminalConnectTypes,
} from '@capacitor-community/stripe-terminal';

import { apiSender, ApiMethods, apiEndpoints } from 'src/utils/axios';
import {
  saveChosenBleReaderToDB,
  getChosenBleReaderFromDB,
  deleteChosenBleReaderFromDB,
} from 'src/utils/Services/DB_Utils';

import { useSystemStore } from 'src/store/SystemStore';

export enum BleReaderStatus {
  NOT_INITIALIZED = 'NOT_INITIALIZED',
  INITIALIZING = 'INITIALIZING',
  INITIALIZED = 'INITIALIZED',
  DISCOVERING = 'DISCOVERING',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  COLLECTING_PAYMENT = 'COLLECTING_PAYMENT',
  PROCESSING_PAYMENT = 'PROCESSING_PAYMENT',
  PAYMENT_COLLECTED = 'PAYMENT_COLLECTED',
  DISCONNECTED = 'DISCONNECTED',
  ERROR = 'ERROR',
}

export enum BlePaymentStatus {
  IDLE = 'IDLE',
  COLLECTING = 'COLLECTING',
  COLLECTED = 'COLLECTED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

interface BleReaderBattery {
  level: number | null;
  isCharging: boolean;
  status: 'nominal' | 'low' | 'critical' | 'unknown';
}

let isGloballyInitialized = false;
let cachedReader: ReaderInterface | null = null;

export function useBleReader() {
  const {
    bleReaderStatus,
    setBleReaderStatus,
    bleReader,
    setBleReader,
    bleReaderBattery,
    setBleReaderBattery,
    blePaymentStatus,
    setBlePaymentStatus,
  } = useSystemStore();

  const listenersRef = useRef<Array<{ remove: () => Promise<void> }>>([]);
  const isInitializing = useRef(false);

  const updateBatteryStatus = useCallback(
    (level: number | null, isCharging = false, batteryStatus?: string) => {
      let status: BleReaderBattery['status'] = 'unknown';

      // If battery status is provided directly (iOS), use it
      if (batteryStatus) {
        status = batteryStatus as BleReaderBattery['status'];
      }
      // Otherwise, derive from percentage level (Android)
      else if (level !== null) {
        if (level < 10) status = 'critical';
        else if (level < 20) status = 'low';
        else status = 'nominal';
      }

      setBleReaderBattery({ level, isCharging, status });
    },
    [setBleReaderBattery]
  );

  const initializeTerminal = useCallback(async () => {
    if (isGloballyInitialized || isInitializing.current) {
      console.log('BLE Reader: Already initialized or initializing');
      return true;
    }

    isInitializing.current = true;
    setBleReaderStatus(BleReaderStatus.INITIALIZING);

    try {
      console.log('BLE Reader: Initializing Stripe Terminal...');
      await StripeTerminal.initialize({ isTest: false });

      isGloballyInitialized = true;
      setBleReaderStatus(BleReaderStatus.INITIALIZED);
      console.log('BLE Reader: Stripe Terminal initialized successfully');

      isInitializing.current = false;
      return true;
    } catch (error: any) {
      console.error('BLE Reader: Initialization failed:', error);
      setBleReaderStatus(BleReaderStatus.ERROR);
      isInitializing.current = false;
      return false;
    }
  }, [setBleReaderStatus]);

  const checkConnectedReader = useCallback(async (): Promise<ReaderInterface | null> => {
    try {
      const { reader } = await StripeTerminal.getConnectedReader();
      if (reader) {
        console.log('BLE Reader: Found existing connection:', reader);
        cachedReader = reader;
        setBleReader(reader);
        setBleReaderStatus(BleReaderStatus.CONNECTED);

        const { batteryLevel, batteryStatus } = reader;
        const { isCharging } = reader || false;
        updateBatteryStatus(batteryLevel, isCharging === 1, batteryStatus);

        return reader;
      }
      return null;
    } catch (error: any) {
      console.log('BLE Reader: No connected reader found');
      return null;
    }
  }, [setBleReader, setBleReaderStatus, updateBatteryStatus]);

  const discoverReaders = useCallback(
    async (locationId: string, preferSavedReader = true): Promise<ReaderInterface[]> => {
      try {
        setBleReaderStatus(BleReaderStatus.DISCOVERING);
        console.log('BLE Reader: Discovering readers...');

        const { readers } = await StripeTerminal.discoverReaders({
          type: TerminalConnectTypes.Bluetooth,
          locationId,
        });

        await new Promise((resolve) => setTimeout(resolve, 2000));

        console.log(`BLE Reader: Found ${readers.length} reader(s)`);

        // Check if we have a saved reader preference
        if (preferSavedReader) {
          const savedReader = await getChosenBleReaderFromDB();
          if (savedReader?.serialNumber) {
            console.log(`BLE Reader: Looking for saved reader ${savedReader.serialNumber}`);

            // Filter to only show the saved reader if it's available
            const savedReaderFound = readers.find(
              (r) => r.serialNumber === savedReader.serialNumber
            );

            if (savedReaderFound) {
              console.log('BLE Reader: Found saved reader in discovery results');
              // Return only the saved reader to auto-connect to it
              return [savedReaderFound];
            }
              console.log('BLE Reader: Saved reader not found in discovery results');

          }
        }

        return readers;
      } catch (error: any) {
        console.error('BLE Reader: Discovery failed:', error);
        setBleReaderStatus(BleReaderStatus.ERROR);
        return [];
      }
    },
    [setBleReaderStatus]
  );

  const connectReader = useCallback(
    async (reader: ReaderInterface, saveAsPreferred = true) => {
      try {
        setBleReaderStatus(BleReaderStatus.CONNECTING);
        const readerLabel = (reader as any).label || reader.serialNumber;
        console.log(`BLE Reader: Connecting to ${readerLabel}...`);

        await StripeTerminal.connectReader({ reader });

        cachedReader = reader;
        setBleReader(reader);
        setBleReaderStatus(BleReaderStatus.CONNECTED);

        const { batteryLevel, batteryStatus } = reader;
        const isCharging = reader.isCharging === 1;
        updateBatteryStatus(batteryLevel, isCharging, batteryStatus);

        // Save this reader as the preferred reader for future connections
        if (saveAsPreferred && reader.serialNumber) {
          try {
            await saveChosenBleReaderToDB({ serialNumber: reader.serialNumber });
            console.log(`BLE Reader: Saved ${reader.serialNumber} as preferred reader`);
          } catch (saveError) {
            console.error('BLE Reader: Failed to save reader preference:', saveError);
            // Don't fail the connection if saving preference fails
          }
        }

        console.log(`BLE Reader: Connected to ${readerLabel}`);
        return true;
      } catch (error: any) {
        console.error('BLE Reader: Connection failed:', error);
        setBleReaderStatus(BleReaderStatus.ERROR);
        return false;
      }
    },
    [setBleReader, setBleReaderStatus, updateBatteryStatus]
  );

  const disconnectReader = useCallback(async (clearSavedReader = false) => {
    try {
      console.log('BLE Reader: Disconnecting...');
      await StripeTerminal.disconnectReader();

      cachedReader = null;
      setBleReader(null);
      setBleReaderStatus(BleReaderStatus.DISCONNECTED);
      setBleReaderBattery({ level: null, isCharging: false, status: 'unknown' });

      // Optionally clear the saved reader preference
      if (clearSavedReader) {
        try {
          await deleteChosenBleReaderFromDB();
          console.log('BLE Reader: Cleared saved reader preference');
        } catch (clearError) {
          console.error('BLE Reader: Failed to clear saved reader preference:', clearError);
        }
      }

      console.log('BLE Reader: Disconnected');
      return true;
    } catch (error: any) {
      console.error('BLE Reader: Disconnect failed:', error);
      return false;
    }
  }, [setBleReader, setBleReaderStatus, setBleReaderBattery]);

  const ensureReaderConnected = useCallback(
    async (locationId: string): Promise<boolean> => {
      if (!isGloballyInitialized) {
        const initialized = await initializeTerminal();
        if (!initialized) return false;
      }

      const reader = await checkConnectedReader();
      if (reader) return true;

      // Check for saved reader preference first
      const savedReader = await getChosenBleReaderFromDB();
      if (savedReader?.serialNumber) {
        console.log(`BLE Reader: Attempting to auto-connect to saved reader ${savedReader.serialNumber}`);
      }

      // This will automatically filter for saved reader if one exists
      const readers = await discoverReaders(locationId);
      if (readers.length === 0) {
        if (savedReader?.serialNumber) {
          console.log('BLE Reader: Saved reader not found, discovering all readers...');
          // Try again without filtering for saved reader
          const allReaders = await discoverReaders(locationId, false);
          if (allReaders.length === 0) {
            console.error('BLE Reader: No readers found');
            return false;
          }
          const selectedReader = allReaders[0];
          return connectReader(selectedReader);
        }
          console.error('BLE Reader: No readers found');
          return false;

      }

      const selectedReader = readers[0];
      return connectReader(selectedReader);
    },
    [initializeTerminal, checkConnectedReader, discoverReaders, connectReader]
  );

  const collectPaymentMethod = useCallback(
    async (paymentIntentId: string) => {
      try {
        if (!cachedReader && !bleReader) {
          console.error('BLE Reader: No reader connected');
          setBlePaymentStatus(BlePaymentStatus.FAILED);
          return false;
        }

        console.log('BLE Reader: Starting payment collection for:', paymentIntentId);
        setBlePaymentStatus(BlePaymentStatus.COLLECTING);
        setBleReaderStatus(BleReaderStatus.COLLECTING_PAYMENT);

        await StripeTerminal.collectPaymentMethod({
          paymentIntent: paymentIntentId,
        });

        console.log('BLE Reader: Payment method collected');
        setBlePaymentStatus(BlePaymentStatus.COLLECTED);
        setBleReaderStatus(BleReaderStatus.PAYMENT_COLLECTED);

        return true;
      } catch (error: any) {
        console.error('BLE Reader: Payment collection failed:', error);

        if (error?.code?.toLowerCase().includes('cancel')) {
          setBlePaymentStatus(BlePaymentStatus.CANCELLED);
        } else {
          setBlePaymentStatus(BlePaymentStatus.FAILED);
        }

        setBleReaderStatus(BleReaderStatus.CONNECTED);
        return false;
      }
    },
    [bleReader, setBlePaymentStatus, setBleReaderStatus]
  );

  const confirmPaymentIntent = useCallback(async () => {
    try {
      console.log('BLE Reader: Confirming payment...');
      setBlePaymentStatus(BlePaymentStatus.PROCESSING);
      setBleReaderStatus(BleReaderStatus.PROCESSING_PAYMENT);

      await StripeTerminal.confirmPaymentIntent();

      console.log('BLE Reader: Payment confirmed');
      setBlePaymentStatus(BlePaymentStatus.COMPLETED);
      setBleReaderStatus(BleReaderStatus.CONNECTED);

      return true;
    } catch (error: any) {
      console.error('BLE Reader: Payment confirmation failed:', error);
      setBlePaymentStatus(BlePaymentStatus.FAILED);
      setBleReaderStatus(BleReaderStatus.CONNECTED);
      return false;
    }
  }, [setBlePaymentStatus, setBleReaderStatus]);

  const cancelPaymentCollection = useCallback(async () => {
    try {
      console.log('BLE Reader: Cancelling payment collection...');
      await StripeTerminal.cancelCollectPaymentMethod();

      setBlePaymentStatus(BlePaymentStatus.CANCELLED);
      setBleReaderStatus(BleReaderStatus.CONNECTED);

      console.log('BLE Reader: Payment collection cancelled');
      return true;
    } catch (error: any) {
      console.error('BLE Reader: Cancel failed:', error);
      return false;
    }
  }, [setBlePaymentStatus, setBleReaderStatus]);

  const processPayment = useCallback(
    async (paymentIntentId: string, locationId: string) => {
      const isConnected = await ensureReaderConnected(locationId);
      if (!isConnected) {
        console.error('BLE Reader: Cannot process payment - reader not connected');
        return false;
      }

      const collected = await collectPaymentMethod(paymentIntentId);
      if (!collected) {
        return false;
      }

      const confirmed = await confirmPaymentIntent();
      return confirmed;
    },
    [ensureReaderConnected, collectPaymentMethod, confirmPaymentIntent]
  );

  const getSavedReaderPreference = useCallback(async () => {
    try {
      const savedReader = await getChosenBleReaderFromDB();
      return savedReader;
    } catch (error) {
      console.error('BLE Reader: Failed to get saved reader preference:', error);
      return null;
    }
  }, []);

  const clearSavedReaderPreference = useCallback(async () => {
    try {
      await deleteChosenBleReaderFromDB();
      console.log('BLE Reader: Cleared saved reader preference');
      return true;
    } catch (error) {
      console.error('BLE Reader: Failed to clear saved reader preference:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    const setupListeners = async () => {
      const listeners = await Promise.all([
        StripeTerminal.addListener(TerminalEventsEnum.RequestedConnectionToken, async () => {
          console.log('BLE Reader: Requesting connection token...');
          try {
            const result = await apiSender(
              apiEndpoints.terminal.ctn_tkn,
              ApiMethods.GET,
              null,
              false
            );
            if (result.secret) {
              await StripeTerminal.setConnectionToken({ token: result.secret });
              console.log('BLE Reader: Connection token set');
            }
          } catch (error: any) {
            console.error('BLE Reader: Failed to fetch connection token:', error);
          }
        }),
        StripeTerminal.addListener(TerminalEventsEnum.ConnectedReader, async () => {
          console.log('BLE Reader: Reader connected event');
          setBleReaderStatus(BleReaderStatus.CONNECTED);
          await checkConnectedReader();
        }),
        StripeTerminal.addListener(TerminalEventsEnum.DisconnectedReader, () => {
          console.log('BLE Reader: Reader disconnected event');
          cachedReader = null;
          setBleReader(null);
          setBleReaderStatus(BleReaderStatus.DISCONNECTED);
          setBleReaderBattery({ level: null, isCharging: false, status: 'unknown' });
        }),
        StripeTerminal.addListener(TerminalEventsEnum.BatteryLevel, (data: any) => {
          if (data?.batteryLevel !== undefined || data?.batteryStatus !== undefined) {
            updateBatteryStatus(data.batteryLevel, data.isCharging || false, data.batteryStatus);
            const statusMsg =
              data.batteryLevel !== null
                ? `${data.batteryLevel}%`
                : data.batteryStatus || 'unknown';
            console.log(
              `BLE Reader: Battery update - ${statusMsg}${data.isCharging ? ' (Charging)' : ''}`
            );
          }
        }),
        StripeTerminal.addListener(TerminalEventsEnum.CollectedPaymentIntent, () => {
          console.log('BLE Reader: Payment method collected event');
          setBlePaymentStatus(BlePaymentStatus.COLLECTED);
        }),
        StripeTerminal.addListener(TerminalEventsEnum.ConfirmedPaymentIntent, () => {
          console.log('BLE Reader: Payment confirmed event');
          setBlePaymentStatus(BlePaymentStatus.COMPLETED);
        }),
        StripeTerminal.addListener(TerminalEventsEnum.Failed, (data: any) => {
          console.error('BLE Reader: Terminal failure event:', data);
          setBleReaderStatus(BleReaderStatus.ERROR);
          setBlePaymentStatus(BlePaymentStatus.FAILED);
        }),
      ]);

      listenersRef.current = listeners;
    };

    setupListeners();

    return () => {
      listenersRef.current.forEach((listener) => listener.remove());
    };
  }, [
    setBleReaderStatus,
    setBleReader,
    setBleReaderBattery,
    setBlePaymentStatus,
    checkConnectedReader,
    updateBatteryStatus,
  ]);

  useEffect(() => {
    const checkOnMount = async () => {
      if (isGloballyInitialized) {
        await checkConnectedReader();
      }
    };
    checkOnMount();
  }, [checkConnectedReader]);

  return {
    status: bleReaderStatus,
    reader: bleReader,
    battery: bleReaderBattery,
    paymentStatus: blePaymentStatus,
    initializeTerminal,
    checkConnectedReader,
    discoverReaders,
    connectReader,
    disconnectReader,
    ensureReaderConnected,
    collectPaymentMethod,
    confirmPaymentIntent,
    cancelPaymentCollection,
    processPayment,
    getSavedReaderPreference,
    clearSavedReaderPreference,
  };
}
